import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import API_URL from '../config/api';
import GameCard from '../components/GameCard';
import useSEO from '../hooks/useSEO';

function Library() {
  useSEO({
    title: 'Library | Nightfall',
    description: 'Your game library - liked games, wishlist, and owned games.',
    url: 'https://nightfallshop.com/library'
  });

  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('liked');
  const [likedGames, setLikedGames] = useState([]);
  const [wishlistGames, setWishlistGames] = useState([]);
  const [ownedGames, setOwnedGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);

  const gameIdInList = (list, gameId) =>
    Array.isArray(list) && list.some((x) => Number(x) === Number(gameId));

  // Load liked and wishlist from API
  useEffect(() => {
    if (authLoading) return;

    const fetchGames = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/games`);
        if (!response.ok) return;
        
        const games = await response.json();
        setAllGames(games);
        
        // Fetch liked and wishlist if authenticated
        if (isAuthenticated && token) {
          try {
            const [likedResponse, wishlistResponse] = await Promise.all([
              fetch(`${API_URL}/library/liked`, {
                headers: { 'Authorization': `Bearer ${token}` }
              }),
              fetch(`${API_URL}/library/wishlist`, {
                headers: { 'Authorization': `Bearer ${token}` }
              })
            ]);
            
            if (likedResponse.ok) {
              const likedData = await likedResponse.json();
              const ids = likedData.gameIds || [];
              const liked = games.filter((g) => gameIdInList(ids, g.id));
              setLikedGames(liked);
            }
            
            if (wishlistResponse.ok) {
              const wishlistData = await wishlistResponse.json();
              const ids = wishlistData.gameIds || [];
              const wishlist = games.filter((g) => gameIdInList(ids, g.id));
              setWishlistGames(wishlist);
            }
          } catch (error) {
            console.error('Error fetching library data:', error);
          }
        } else {
          // Logged out: only reset this page's React state so we do not show the last
          // signed in user's lists. Nothing is deleted on the server; data loads again after sign in.
          setLikedGames([]);
          setWishlistGames([]);
        }
      } catch (error) {
        console.error('Error fetching games:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [isAuthenticated, token, authLoading]);

  // Fetch owned games
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setOwnedGames([]);
      return;
    }

    const fetchOwnedGames = async () => {
      try {
        const response = await fetch(`${API_URL}/purchases/games`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          const ownedIds = data.games.map((g) => g.gameId);
          
          // Get full game objects (normalize id types from API)
          const owned = allGames.filter((game) =>
            ownedIds.some((oid) => Number(oid) === Number(game.id))
          );
          setOwnedGames(owned);
        }
      } catch (error) {
        console.error('Error fetching owned games:', error);
      }
    };

    if (allGames.length > 0) {
      fetchOwnedGames();
    }
  }, [isAuthenticated, token, allGames]);

  const tabs = [
    { id: 'liked', label: 'Liked', count: likedGames.length },
    { id: 'wishlist', label: 'Wishlist', count: wishlistGames.length },
    { id: 'owned', label: 'Owned', count: ownedGames.length }
  ];

  const getCurrentGames = () => {
    switch (activeTab) {
      case 'liked':
        return likedGames;
      case 'wishlist':
        return wishlistGames;
      case 'owned':
        return ownedGames;
      default:
        return [];
    }
  };

  if (loading) {
    return (
      <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
        <div className="text-white text-center py-20">Loading library...</div>
      </main>
    );
  }

  const currentGames = getCurrentGames();

  return (
    <main className="p-2 lg:p-5 pt-0 flex flex-col w-full max-w-full overflow-x-hidden">
      <style>{`
        .library-align {
          margin-top: calc(5.75rem + 0.5rem - 6rem);
        }
        @media (min-width: 768px) {
          .library-align {
            margin-top: calc(5.75rem + 1.25rem - 8rem);
          }
        }
      `}</style>
      
      <div className="library-align mb-6 lg:mb-8">
        <h2 className="font-semibold text-3xl lg:text-4xl text-white">Library</h2>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="flex gap-2 border-b border-dark-border/50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 font-medium text-sm transition-all duration-200 relative ${
                activeTab === tab.id
                  ? 'text-white'
                  : 'text-white/60 hover:text-white/80'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-accent-blue/20 text-accent-blue'
                    : 'bg-white/10 text-white/60'
                }`}>
                  {tab.count}
                </span>
              )}
              {activeTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-blue"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="w-full">
        {currentGames.length === 0 ? (
          <div className="bg-dark-card border border-dark-border rounded-3xl p-16 text-center">
            <div className="w-20 h-20 mx-auto mb-6 bg-dark-bg/50 rounded-full flex items-center justify-center">
              {activeTab === 'liked' && (
                <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              )}
              {activeTab === 'wishlist' && (
                <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              )}
              {activeTab === 'owned' && (
                <svg className="w-10 h-10 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              )}
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {activeTab === 'liked' && 'No liked games yet'}
              {activeTab === 'wishlist' && 'Your wishlist is empty'}
              {activeTab === 'owned' && (isAuthenticated ? 'No owned games yet' : 'Sign in to view your owned games')}
            </h3>
            <p className="text-white/60 mb-8 max-w-md mx-auto">
              {activeTab === 'liked' && 'Start liking games to see them here!'}
              {activeTab === 'wishlist' && 'Add games to your wishlist to keep track of them.'}
              {activeTab === 'owned' && (isAuthenticated ? 'Purchase games to add them to your library.' : 'Sign in to see your purchased games.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 w-full max-w-full">
            {currentGames.map(game => (
              <GameCard 
                key={game.id} 
                game={game} 
                isOwned={activeTab === 'owned' || ownedGames.some(g => g.id === game.id)}
              />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}

export default Library;

